import { ReactElement } from 'react';

import AddTriggerForm from './AddTrigger';
import AddMedicationForm from './AddMedication';
import AddMedicationDeliveryForm from './AddMedicationDelivery';
import AddDeEscalationForm from './AddDeEscalation';
import AddAllergyForm from './AddAllergy';
import AddAddressForm from './AddAddress';
import AddPhoneForm from './AddPhone';
import AddTeamForm from './AddTeam';
import AddTreatmentPlanForm from './AddTreatmentPlan';
import AddSecondaryDiagnosisForm from './AddSecondaryDiagnosis';
import EditPrimaryDiagnosisForm from './EditPrimaryDiagnosis';
import AddInjectableMedicationDelivery from './AddInjectableMedicationDelivery';
import {
  formatTriggerDefaultValues,
  formatMedicationDefaultValues,
  formatMedicationDeliveryDefaultValues,
  formatDeEsclationDefaultValues,
  formatSecondaryDiagnosisDefaultValues,
  formatPrimaryDiagnosisDefaultValues,
  formatAllergyDefaultValues,
  formatAddressDefaultValues,
  formatPhoneDefaultValues,
  formatTreatmentPlanDefaultValues,
  formatTeamDefaultValues,
} from '~/utils/formatDefaultValues';

import { IDiagnosis } from '~/services/api/client/types';
import {
  IClientAllergyResponse,
  IClientDeEscalationResponse,
  IClientMedicationDeliveryResponse,
  IClientMedicationResponse,
  IClientPhone,
  IClientSecondaryDiagnosisResponse,
  IClientTeam,
  IClientTreatmentPlanResponse,
  IClientTriggerResponse,
} from '~/services/api/clientDetails/types';
import { IAddressExtended } from '~/services/api/types';
import { IRequestInfo } from './types';
import { IDetails } from '../types';

interface IProps {
  formType: string;
  setModalTitle: (str: string) => void;
  details: IDetails;
  setDetails: (arg: IDetails) => void;
  requestInfo: IRequestInfo;
  showSecondSubmit: boolean;
  // fed by several unrelated parent features/store slices depending on formType,
  // each format*DefaultValues call below casts to the shape it actually expects
  current: unknown;
  primaryDiagnosis: IDiagnosis | null;
  showCurrentTeamVisits?: boolean;
}

const Forms = ({
  formType,
  current,
  primaryDiagnosis,
  showCurrentTeamVisits,
  ...rest
}: IProps): ReactElement => {
  const { showSecondSubmit, details, ...sanitaizedProps } = rest;

  return (
    <>
      {formType === 'trigger' && (
        <AddTriggerForm
          {...rest}
          defaultValues={
            current ? formatTriggerDefaultValues(current as IClientTriggerResponse) : undefined
          }
        />
      )}
      {formType === 'medication' && (
        <AddMedicationForm
          {...rest}
          defaultValues={
            current
              ? formatMedicationDefaultValues(current as IClientMedicationResponse)
              : undefined
          }
        />
      )}
      {formType === 'medicationDelivery' && (
        <AddMedicationDeliveryForm
          {...rest}
          showCurrentTeamVisits={showCurrentTeamVisits}
          defaultValues={
            current
              ? formatMedicationDeliveryDefaultValues(
                  current as IClientMedicationDeliveryResponse,
                  (current as IClientMedicationDeliveryResponse & { team?: { id?: string } })?.team
                    ?.id ?? rest.requestInfo.teamId,
                )
              : undefined
          }
        />
      )}
      {formType === 'injectableMedicationDelivery' && (
        <AddInjectableMedicationDelivery
          {...rest}
          showCurrentTeamVisits={showCurrentTeamVisits}
          defaultValues={
            current
              ? formatMedicationDeliveryDefaultValues(
                  current as IClientMedicationDeliveryResponse,
                  (current as IClientMedicationDeliveryResponse & { team?: { id?: string } })?.team
                    ?.id ?? rest.requestInfo.teamId,
                )
              : undefined
          }
        />
      )}
      {formType === 'deEscalation' && (
        <AddDeEscalationForm
          {...rest}
          defaultValues={
            current
              ? formatDeEsclationDefaultValues(current as IClientDeEscalationResponse)
              : undefined
          }
        />
      )}
      {formType === 'allergy' && (
        <AddAllergyForm
          {...rest}
          defaultValues={
            current ? formatAllergyDefaultValues(current as IClientAllergyResponse) : undefined
          }
        />
      )}
      {formType === 'secondaryDiagnosis' && (
        <AddSecondaryDiagnosisForm
          {...rest}
          defaultValues={
            current
              ? formatSecondaryDiagnosisDefaultValues(current as IClientSecondaryDiagnosisResponse)
              : undefined
          }
        />
      )}
      {formType === 'primaryDiagnosis' && (
        <EditPrimaryDiagnosisForm
          {...sanitaizedProps}
          defaultValues={
            primaryDiagnosis ? formatPrimaryDiagnosisDefaultValues(primaryDiagnosis) : undefined
          }
        />
      )}
      {formType === 'address' && (
        <AddAddressForm
          {...rest}
          defaultValues={
            current ? formatAddressDefaultValues(current as IAddressExtended) : undefined
          }
        />
      )}
      {formType === 'phone' && (
        <AddPhoneForm
          {...rest}
          defaultValues={current ? formatPhoneDefaultValues(current as IClientPhone) : undefined}
        />
      )}
      {formType === 'team' && (
        <AddTeamForm
          {...rest}
          defaultValues={current ? formatTeamDefaultValues(current as IClientTeam) : undefined}
        />
      )}
      {formType === 'treatmentPlan' && (
        <AddTreatmentPlanForm
          {...rest}
          defaultValues={
            current
              ? formatTreatmentPlanDefaultValues(current as IClientTreatmentPlanResponse)
              : undefined
          }
        />
      )}
    </>
  );
};

export default Forms;
